import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft,
  BarChart3,
  Headphones,
  Lock,
  Mail,
  MessageCircle,
  Megaphone,
  Smartphone,
} from 'lucide-react';
import { authApi, marketingApi } from '@/services/api';
import OrderNotificationsPanel from '@/components/settings/OrderNotificationsPanel';
import SetupGuidePanel from '@/components/settings/SetupGuidePanel';
import { getMarketingSetupGuide } from '@/components/settings/setupGuides';
import toast from 'react-hot-toast';

type ConfigureId = 'whatsapp' | 'sms' | 'email' | 'notifications';

type CardDef = {
  id: ConfigureId | 'campaigns' | 'usage';
  title: string;
  blurb: string;
  icon: typeof MessageCircle;
  unlocked: boolean;
  configure?: ConfigureId;
  href?: string;
  supportSubject?: string;
  connected?: boolean;
};

const PROVIDER_LABELS: Record<string, string> = {
  meta_cloud: 'Meta Cloud API',
  twilio_whatsapp: 'Twilio WhatsApp',
  twilio_sms: 'Twilio SMS',
  vonage: 'Vonage',
  firetext: 'FireText',
  smtp: 'SMTP',
  sendgrid: 'SendGrid',
  mailgun: 'Mailgun',
};

type AccountForm = {
  name?: string;
  channel: string;
  provider: string;
  label: string;
  is_default: boolean;
  from_identity: string;
  phone_number_id: string;
  smtp_host: string;
  smtp_port: number;
  smtp_user: string;
  smtp_use_tls: boolean;
  from_email: string;
  from_name: string;
  api_key: string;
  api_secret: string;
  access_token: string;
  smtp_password: string;
  test_to: string;
};

function emptyForm(channel: string, provider: string): AccountForm {
  return {
    channel,
    provider,
    label: '',
    is_default: true,
    from_identity: '',
    phone_number_id: '',
    smtp_host: '',
    smtp_port: 587,
    smtp_user: '',
    smtp_use_tls: true,
    from_email: '',
    from_name: '',
    api_key: '',
    api_secret: '',
    access_token: '',
    smtp_password: '',
    test_to: '',
  };
}

function ChannelConfigurePanel({
  channel,
  onBack,
}: {
  channel: 'WhatsApp' | 'SMS' | 'Email';
  onBack: () => void;
}) {
  const qc = useQueryClient();
  const { data } = useQuery({
    queryKey: ['marketingChannels'],
    queryFn: async () => (await marketingApi.listChannels()).data.message,
  });

  const providers: string[] = data?.providers?.[channel] || [];
  const existing = (data?.accounts || []).find(
    (a: { channel: string; is_default?: number }) => a.channel === channel && a.is_default
  ) || (data?.accounts || []).find((a: { channel: string }) => a.channel === channel);

  const [form, setForm] = useState<AccountForm>(() => emptyForm(channel, providers[0] || ''));

  useEffect(() => {
    if (!data) return;
    const provs: string[] = data?.providers?.[channel] || [];
    const acct =
      (data?.accounts || []).find(
        (a: { channel: string; is_default?: number }) => a.channel === channel && a.is_default
      ) || (data?.accounts || []).find((a: { channel: string }) => a.channel === channel);
    if (acct) {
      setForm({
        ...emptyForm(channel, acct.provider || provs[0] || ''),
        name: acct.name,
        provider: acct.provider,
        label: acct.label || '',
        is_default: Boolean(acct.is_default),
        from_identity: acct.from_identity || '',
        phone_number_id: acct.phone_number_id || '',
        smtp_host: acct.smtp_host || '',
        smtp_port: Number(acct.smtp_port || 587),
        smtp_user: acct.smtp_user || '',
        smtp_use_tls: Boolean(acct.smtp_use_tls ?? true),
        from_email: acct.from_email || '',
        from_name: acct.from_name || '',
      });
    } else if (provs[0]) {
      setForm((f) => ({ ...f, provider: f.provider || provs[0] }));
    }
  }, [data, channel]);

  const saveMut = useMutation({
    mutationFn: () => {
      const payload: Record<string, unknown> = { ...form };
      // Omit empty secrets so we don't wipe stored passwords
      for (const k of ['api_key', 'api_secret', 'access_token', 'smtp_password']) {
        if (!String(payload[k] || '').trim()) delete payload[k];
      }
      delete payload.test_to;
      return marketingApi.saveChannel(payload);
    },
    onSuccess: (res) => {
      toast.success('Channel saved');
      const acct = res.data.message;
      if (acct?.name) setForm((f) => ({ ...f, name: acct.name }));
      qc.invalidateQueries({ queryKey: ['marketingChannels'] });
    },
    onError: (e: unknown) => {
      const msg =
        (e as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'Save failed';
      toast.error(String(msg));
    },
  });

  const testMut = useMutation({
    mutationFn: async () => {
      let name = form.name || '';
      if (!name) {
        const saved = await saveMut.mutateAsync();
        name = String(saved.data.message?.name || '');
      }
      if (!name) throw new Error('Save the channel before testing');
      return marketingApi.testChannel({ name, to: form.test_to || undefined });
    },
    onSuccess: (res) => {
      const result = res.data.message?.result;
      if (result?.success) toast.success(result.dry_run ? 'Dry run OK' : 'Test sent');
      else toast.error(result?.error || 'Test failed');
      qc.invalidateQueries({ queryKey: ['marketingChannels'] });
    },
    onError: (e: unknown) => {
      const msg =
        (e as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'Test failed';
      toast.error(String(msg));
    },
  });

  const set = (key: keyof AccountForm, value: string | number | boolean) =>
    setForm((f) => ({ ...f, [key]: value }));

  const guide = getMarketingSetupGuide(channel, form.provider || providers[0] || '');

  return (
    <div className="space-y-4 min-w-0">
      <button
        type="button"
        onClick={onBack}
        className="inline-flex items-center gap-1.5 text-sm text-gray-600 hover:text-gray-900"
      >
        <ArrowLeft className="w-4 h-4" /> Back to Marketing
      </button>
      <div>
        <h2 className="text-lg font-semibold text-gray-900">Configure {channel}</h2>
        <p className="text-sm text-gray-500 mt-1">
          Credentials stay on your Marketing Channel Account — separate from WhatsApp Shop / Marketplaces.
        </p>
      </div>

      <SetupGuidePanel guide={guide} accent="teal" />

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <label className="text-sm sm:col-span-2">
          <span className="font-medium text-gray-700">Provider</span>
          <select
            className="mt-1 w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
            value={form.provider}
            onChange={(e) => set('provider', e.target.value)}
          >
            {providers.map((p) => (
              <option key={p} value={p}>
                {PROVIDER_LABELS[p] || p}
              </option>
            ))}
          </select>
        </label>

        <label className="text-sm">
          <span className="font-medium text-gray-700">Label</span>
          <input
            className="mt-1 w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
            value={form.label}
            onChange={(e) => set('label', e.target.value)}
          />
        </label>

        {channel === 'WhatsApp' && form.provider === 'meta_cloud' && (
          <>
            <label className="text-sm sm:col-span-2">
              <span className="font-medium text-gray-700">Access token</span>
              <input
                type="password"
                className="mt-1 w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
                placeholder={existing?.has_access_token ? '•••••••• (unchanged)' : ''}
                value={form.access_token}
                onChange={(e) => set('access_token', e.target.value)}
              />
            </label>
            <label className="text-sm sm:col-span-2">
              <span className="font-medium text-gray-700">Phone Number ID</span>
              <input
                className="mt-1 w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
                value={form.phone_number_id}
                onChange={(e) => set('phone_number_id', e.target.value)}
              />
            </label>
          </>
        )}

        {(channel === 'WhatsApp' && form.provider === 'twilio_whatsapp') ||
        channel === 'SMS' ? (
          <>
            <label className="text-sm">
              <span className="font-medium text-gray-700">
                {form.provider.includes('twilio') ? 'Account SID' : 'API key'}
              </span>
              <input
                type="password"
                className="mt-1 w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
                placeholder={existing?.has_api_key ? '•••••••• (unchanged)' : ''}
                value={form.api_key}
                onChange={(e) => set('api_key', e.target.value)}
              />
            </label>
            {form.provider !== 'firetext' && (
              <label className="text-sm">
                <span className="font-medium text-gray-700">
                  {form.provider.includes('twilio') ? 'Auth token' : 'API secret'}
                </span>
                <input
                  type="password"
                  className="mt-1 w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
                  placeholder={existing?.has_api_secret ? '•••••••• (unchanged)' : ''}
                  value={form.api_secret}
                  onChange={(e) => set('api_secret', e.target.value)}
                />
              </label>
            )}
            <label className="text-sm sm:col-span-2">
              <span className="font-medium text-gray-700">From / Sender</span>
              <input
                className="mt-1 w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
                placeholder={
                  form.provider === 'twilio_whatsapp' ? 'whatsapp:+44…' : '+44… or sender ID'
                }
                value={form.from_identity}
                onChange={(e) => set('from_identity', e.target.value)}
              />
            </label>
          </>
        ) : null}

        {channel === 'Email' && (
          <>
            {(form.provider === 'sendgrid' || form.provider === 'mailgun') && (
              <label className="text-sm sm:col-span-2">
                <span className="font-medium text-gray-700">API key</span>
                <input
                  type="password"
                  className="mt-1 w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
                  placeholder={existing?.has_api_key ? '•••••••• (unchanged)' : ''}
                  value={form.api_key}
                  onChange={(e) => set('api_key', e.target.value)}
                />
              </label>
            )}
            {form.provider === 'mailgun' && (
              <label className="text-sm sm:col-span-2">
                <span className="font-medium text-gray-700">Mailgun domain</span>
                <input
                  className="mt-1 w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
                  value={form.smtp_host}
                  onChange={(e) => set('smtp_host', e.target.value)}
                  placeholder="mg.example.com"
                />
              </label>
            )}
            {form.provider === 'smtp' && (
              <>
                <label className="text-sm">
                  <span className="font-medium text-gray-700">SMTP host</span>
                  <input
                    className="mt-1 w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
                    value={form.smtp_host}
                    onChange={(e) => set('smtp_host', e.target.value)}
                    placeholder="smtp.gmail.com"
                  />
                </label>
                <label className="text-sm">
                  <span className="font-medium text-gray-700">Port</span>
                  <input
                    type="number"
                    className="mt-1 w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
                    value={form.smtp_port}
                    onChange={(e) => set('smtp_port', Number(e.target.value) || 587)}
                  />
                </label>
                <label className="text-sm">
                  <span className="font-medium text-gray-700">SMTP user</span>
                  <input
                    className="mt-1 w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
                    value={form.smtp_user}
                    onChange={(e) => set('smtp_user', e.target.value)}
                  />
                </label>
                <label className="text-sm">
                  <span className="font-medium text-gray-700">SMTP password</span>
                  <input
                    type="password"
                    className="mt-1 w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
                    placeholder={existing?.has_smtp_password ? '•••••••• (unchanged)' : ''}
                    value={form.smtp_password}
                    onChange={(e) => set('smtp_password', e.target.value)}
                  />
                </label>
                <label className="flex items-center gap-2 text-sm sm:col-span-2 mt-1">
                  <input
                    type="checkbox"
                    checked={form.smtp_use_tls}
                    onChange={(e) => set('smtp_use_tls', e.target.checked)}
                  />
                  Use TLS
                </label>
              </>
            )}
            <label className="text-sm">
              <span className="font-medium text-gray-700">From email</span>
              <input
                className="mt-1 w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
                value={form.from_email}
                onChange={(e) => set('from_email', e.target.value)}
              />
            </label>
            <label className="text-sm">
              <span className="font-medium text-gray-700">From name</span>
              <input
                className="mt-1 w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
                value={form.from_name}
                onChange={(e) => set('from_name', e.target.value)}
              />
            </label>
          </>
        )}

        <label className="text-sm sm:col-span-2">
          <span className="font-medium text-gray-700">
            Test recipient ({channel === 'Email' ? 'email' : 'mobile'})
          </span>
          <input
            className="mt-1 w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
            value={form.test_to}
            onChange={(e) => set('test_to', e.target.value)}
            placeholder={channel === 'Email' ? 'you@shop.com' : '+447…'}
          />
        </label>
      </div>

      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          className="btn-primary text-sm"
          disabled={saveMut.isPending}
          onClick={() => saveMut.mutate()}
        >
          Save
        </button>
        <button
          type="button"
          className="btn-secondary text-sm"
          disabled={testMut.isPending}
          onClick={() => testMut.mutate()}
        >
          Send test
        </button>
      </div>
      {existing?.status && (
        <p className="text-xs text-gray-500">
          Status: {existing.status}
          {existing.last_test_at ? ` · Last test ${existing.last_test_at}` : ''}
        </p>
      )}
    </div>
  );
}

/**
 * Marketing hub — WhatsApp / SMS / Email add-ons (separate from Marketplaces WhatsApp Shop).
 */
export default function MarketingHubTab() {
  const [params, setParams] = useSearchParams();
  const configure = params.get('configure') as ConfigureId | null;

  const { data: profile } = useQuery({
    queryKey: ['profile'],
    queryFn: async () => (await authApi.getCompanyProfile()).data.message,
    staleTime: 60_000,
  });

  const { data: channelPayload } = useQuery({
    queryKey: ['marketingChannels'],
    queryFn: async () => (await marketingApi.listChannels()).data.message,
    staleTime: 30_000,
    retry: 1,
  });

  const features: string[] = Array.isArray(profile?.features) ? profile.features : [];
  const wa =
    Boolean(profile?.allow_marketing_whatsapp) || features.includes('allow_marketing_whatsapp');
  const sms =
    Boolean(profile?.allow_marketing_sms) || features.includes('allow_marketing_sms');
  const email =
    Boolean(profile?.allow_marketing_email) || features.includes('allow_marketing_email');
  const anyUnlocked = wa || sms || email;

  const connected = channelPayload?.connected || {};

  const setConfigure = (id: ConfigureId | null) => {
    const next = new URLSearchParams(params);
    next.set('tab', 'marketing');
    if (id) next.set('configure', id);
    else next.delete('configure');
    setParams(next, { replace: true });
  };

  const cards: CardDef[] = useMemo(
    () => [
      {
        id: 'whatsapp',
        title: 'Marketing WhatsApp',
        blurb: 'Outbound promotions and order updates. Separate from WhatsApp Shop catalogue.',
        icon: MessageCircle,
        unlocked: wa,
        configure: 'whatsapp',
        supportSubject: 'Enable Marketing WhatsApp',
        connected: Boolean(connected.WhatsApp),
      },
      {
        id: 'sms',
        title: 'SMS',
        blurb: 'UK SMS via Twilio, Vonage or FireText — broadcasts and order alerts.',
        icon: Smartphone,
        unlocked: sms,
        configure: 'sms',
        supportSubject: 'Enable Marketing SMS',
        connected: Boolean(connected.SMS),
      },
      {
        id: 'email',
        title: 'Email',
        blurb: 'Send with your SMTP, SendGrid or Mailgun credentials.',
        icon: Mail,
        unlocked: email,
        configure: 'email',
        supportSubject: 'Enable Marketing Email',
        connected: Boolean(connected.Email),
      },
      {
        id: 'notifications',
        title: 'Order notifications',
        blurb: 'Toggle placed / accepted / ready / delivery / collected messages.',
        icon: Megaphone,
        unlocked: anyUnlocked,
        configure: 'notifications',
        supportSubject: 'Enable a Marketing channel',
      },
      {
        id: 'campaigns',
        title: 'Campaigns',
        blurb: 'Filter customers by type, city or postcode and broadcast an offer.',
        icon: Megaphone,
        unlocked: anyUnlocked,
        href: '/marketing',
        supportSubject: 'Enable a Marketing channel',
      },
      {
        id: 'usage',
        title: 'Message usage',
        blurb: 'See how many WhatsApp, SMS and Email messages you have sent and consumed.',
        icon: BarChart3,
        unlocked: anyUnlocked,
        href: '/marketing/usage',
        supportSubject: 'Enable a Marketing channel',
      },
    ],
    [wa, sms, email, anyUnlocked, connected]
  );

  if (configure === 'whatsapp' && wa) {
    return <ChannelConfigurePanel channel="WhatsApp" onBack={() => setConfigure(null)} />;
  }
  if (configure === 'sms' && sms) {
    return <ChannelConfigurePanel channel="SMS" onBack={() => setConfigure(null)} />;
  }
  if (configure === 'email' && email) {
    return <ChannelConfigurePanel channel="Email" onBack={() => setConfigure(null)} />;
  }
  if (configure === 'notifications' && anyUnlocked) {
    return (
      <div className="space-y-4">
        <button
          type="button"
          onClick={() => setConfigure(null)}
          className="inline-flex items-center gap-1.5 text-sm text-gray-600 hover:text-gray-900"
        >
          <ArrowLeft className="w-4 h-4" /> Back to Marketing
        </button>
        <OrderNotificationsPanel />
      </div>
    );
  }

  return (
    <div className="space-y-6 min-w-0">
      <div>
        <h2 className="text-lg font-semibold text-gray-900">Marketing</h2>
        <p className="text-sm text-gray-500 mt-1 max-w-2xl">
          WhatsApp, SMS and Email are separate billed add-ons. Configure your own provider credentials,
          then send order updates or customer broadcasts. WhatsApp Shop (catalogue) stays under
          Marketplaces.
        </p>
        <p className="text-xs text-amber-800 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2 mt-3 max-w-2xl">
          Promotional sends may require customer consent under UK PECR/GDPR. Only customers with a
          mobile or email on file are messaged.
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3 sm:gap-4">
        {cards.map((c) => {
          const Icon = c.icon;
          const locked = !c.unlocked;
          const supportHref = `/support${
            c.supportSubject ? `?subject=${encodeURIComponent(c.supportSubject)}` : ''
          }`;
          return (
            <div
              key={c.id}
              className={`bg-white border rounded-xl p-4 sm:p-5 flex flex-col gap-3 min-w-0 ${
                locked ? 'border-amber-200/80' : 'border-gray-200'
              }`}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-center gap-2 min-w-0">
                  <div
                    className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${
                      locked ? 'bg-amber-50' : 'bg-gray-100'
                    }`}
                  >
                    {locked ? (
                      <Lock className="w-4 h-4 text-amber-700" />
                    ) : (
                      <Icon className="w-4 h-4 text-gray-600" />
                    )}
                  </div>
                  <h3 className="font-semibold text-gray-900 truncate">{c.title}</h3>
                </div>
                <span
                  className={`text-xs font-medium px-2 py-0.5 rounded-full shrink-0 ${
                    locked
                      ? 'bg-amber-50 text-amber-800'
                      : c.connected
                        ? 'bg-emerald-50 text-emerald-700'
                        : 'bg-sky-50 text-sky-700'
                  }`}
                >
                  {locked ? 'Add-on' : c.connected ? 'Connected' : 'Included'}
                </span>
              </div>
              <p className="text-sm text-gray-600 flex-1">{c.blurb}</p>
              {locked && (
                <p className="text-xs text-amber-800 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2">
                  Not included in your package. Contact Support to purchase this add-on.
                </p>
              )}
              <div className="flex flex-wrap gap-2 mt-auto">
                {!locked && c.href ? (
                  <Link to={c.href} className="btn-primary text-sm w-fit">
                    Open
                  </Link>
                ) : null}
                {!locked && c.configure ? (
                  <button
                    type="button"
                    onClick={() => setConfigure(c.configure!)}
                    className="btn-primary text-sm w-fit"
                  >
                    Configure
                  </button>
                ) : null}
                {locked ? (
                  <Link
                    to={supportHref}
                    className="btn-secondary text-sm inline-flex items-center gap-1.5 w-fit"
                  >
                    <Headphones className="w-3.5 h-3.5" />
                    Contact Support
                  </Link>
                ) : null}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
